/**
 * nb-sections.js
 * Shared interactivity for all custom nb- homepage sections.
 * Handles: counter animation, mode strip animation, feature tabs, portrait flip cards.
 * Works in both live site and Shopify theme editor (re-initializes on section load).
 */

(function() {
  'use strict';

  /* ─── COUNTER ANIMATION (nb-problem) ─────────────────── */
  function initCounters(scope) {
    var counters = scope.querySelectorAll('.nb-problem-num[data-counter-target]');
    counters.forEach(function(numEl) {
      if (numEl.dataset.nbInit) return;
      numEl.dataset.nbInit = '1';

      var target = parseInt(numEl.dataset.counterTarget, 10) || 2500;
      var suffix = numEl.dataset.counterSuffix || '';
      var animated = false;

      function animateCounter() {
        if (animated) return;
        animated = true;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          numEl.textContent = target.toLocaleString() + suffix;
          return;
        }

        var duration = 2000;
        var start = performance.now();

        function tick(now) {
          var elapsed = now - start;
          var progress = Math.min(elapsed / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 3);
          var current = Math.round(eased * target);
          numEl.textContent = current.toLocaleString() + suffix;
          if (progress < 1) requestAnimationFrame(tick);
        }

        requestAnimationFrame(tick);
      }

      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            animateCounter();
            observer.disconnect();
          }
        });
      }, { threshold: 0.3 });

      observer.observe(numEl);
    });
  }

  /* ─── MODE STRIP ANIMATION (nb-product-intro) ────────── */
  function initModeStrips(scope) {
    var strips = scope.querySelectorAll('.nb-pi-mode-strip');
    strips.forEach(function(strip) {
      if (strip.dataset.nbInit) return;
      strip.dataset.nbInit = '1';

      var items = strip.querySelectorAll('.nb-pi-mode-item');
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            items.forEach(function(item) { item.classList.add('visible'); });
            observer.disconnect();
          }
        });
      }, { threshold: 0.3 });

      observer.observe(strip);
    });
  }

  /* ─── FEATURE TABS (nb-feature-tabs) ─────────────────── */
  function initFeatureTabs(scope) {
    var containers = scope.querySelectorAll('[data-section-type="nb-feature-tabs"]');
    containers.forEach(function(section) {
      var layout = section.querySelector('.nb-ft-layout');
      if (!layout || layout.dataset.nbInit) return;
      layout.dataset.nbInit = '1';

      layout.addEventListener('click', function(e) {
        var tabItem = e.target.closest('.nb-ft-tab-item');
        if (!tabItem) return;

        e.preventDefault();
        e.stopPropagation();

        var targetId = tabItem.dataset.tabTarget;

        /* Deactivate all tab items and images */
        layout.querySelectorAll('.nb-ft-tab-item').forEach(function(t) { t.classList.remove('active'); });
        layout.querySelectorAll('.nb-ft-image').forEach(function(img) { img.classList.remove('active'); });

        /* Activate clicked tab and its image */
        tabItem.classList.add('active');
        var image = document.getElementById(targetId);
        if (image) image.classList.add('active');
      });
    });
  }

  /* ─── PORTRAIT FLIP CARDS (nb-real-parents) ──────────── */
  function initFlipCards(scope) {
    var sections = scope.querySelectorAll('[data-section-type="nb-real-parents"]');
    sections.forEach(function(section) {
      if (section.dataset.nbFlipInit) return;
      section.dataset.nbFlipInit = '1';

      section.addEventListener('click', function(e) {
        var card = e.target.closest('.nb-rp-card');
        if (!card) return;

        e.preventDefault();
        e.stopPropagation();
        card.classList.toggle('flipped');
      });
    });
  }

  /* ─── MODES TOGGLE (nb-feature-alternating) ──────────── */
  function initModesToggle(scope) {
    var triggers = scope.querySelectorAll('[data-nb-modes-toggle]');
    triggers.forEach(function(trigger) {
      if (trigger.dataset.nbInit) return;
      trigger.dataset.nbInit = '1';

      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        var body = trigger.nextElementSibling;
        if (!body) return;

        var isOpen = trigger.classList.contains('open');
        trigger.classList.toggle('open');
        body.classList.toggle('open');
      });
    });
  }

  /* ─── QUICK ADD TO CART (nb-accessories-upsell) ─────── */
  function initQuickAdd(scope) {
    var buttons = scope.querySelectorAll('[data-nb-quick-add]');
    buttons.forEach(function(btn) {
      if (btn.dataset.nbInit) return;
      btn.dataset.nbInit = '1';

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var variantId = btn.dataset.variantId;
        if (!variantId || btn.disabled) return;

        var originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        })
        .then(function(res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function() {
          btn.textContent = 'Added ✓';
          setTimeout(function() {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);

          /* Trigger Minimog cart drawer if available */
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          var cartIcon = document.querySelector('[data-cart-toggle], .m-cart-icon, .header__icon--cart');
          if (cartIcon) cartIcon.click();
        })
        .catch(function() {
          btn.textContent = 'Error';
          setTimeout(function() {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2000);
        });
      });
    });
  }

  /* ─── FAQ PAGE SEARCH + ACCORDION (nb-faq-page) ──────── */
  function initFaqPage(scope) {
    var sections = scope.querySelectorAll('[data-section-type="nb-faq-page"]');
    sections.forEach(function(section) {
      if (section.dataset.nbFaqInit) return;
      section.dataset.nbFaqInit = '1';

      var searchInput = section.querySelector('[data-faq-search]');
      var noResults = section.querySelector('[data-faq-no-results]');
      var categories = section.querySelectorAll('[data-faq-category]');
      var items = section.querySelectorAll('[data-faq-item]');

      /* Accordion toggle via event delegation */
      section.addEventListener('click', function(e) {
        var trigger = e.target.closest('[data-faq-trigger]');
        if (!trigger) return;

        e.preventDefault();
        var item = trigger.closest('.nb-accordion-item');
        var isOpen = item.classList.contains('open');

        if (isOpen) {
          item.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      /* JetBrains-style rail tabs: switch active category */
      var railBtns = section.querySelectorAll('[data-faq-rail-btn]');
      function activateCategory(targetId) {
        railBtns.forEach(function(b) {
          var isActive = b.getAttribute('data-faq-target') === targetId;
          b.classList.toggle('active', isActive);
          b.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        categories.forEach(function(cat) {
          cat.classList.toggle('active', cat.getAttribute('data-faq-cat-id') === targetId);
        });
      }
      railBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var targetId = btn.getAttribute('data-faq-target');
          if (targetId) activateCategory(targetId);
        });
      });

      /* Search filtering — expands all categories while a query is active */
      if (searchInput) {
        searchInput.addEventListener('input', function() {
          var query = searchInput.value.toLowerCase().trim();
          var visibleCount = 0;

          items.forEach(function(item) {
            var question = (item.querySelector('[data-faq-question]') || {}).textContent || '';
            var answer = (item.querySelector('[data-faq-answer]') || {}).textContent || '';
            var match = !query || question.toLowerCase().indexOf(query) !== -1 || answer.toLowerCase().indexOf(query) !== -1;

            if (match) {
              item.classList.remove('search-hidden');
              visibleCount++;
            } else {
              item.classList.add('search-hidden');
            }
          });

          /* While searching: show every category that has results. When cleared: revert to active-only. */
          var searching = query.length > 0;
          section.classList.toggle('nb-faqp-searching', searching);
          categories.forEach(function(cat) {
            var visibleItems = cat.querySelectorAll('.nb-accordion-item:not(.search-hidden)');
            if (searching) {
              if (visibleItems.length === 0) {
                cat.classList.add('search-hidden');
              } else {
                cat.classList.remove('search-hidden');
              }
            } else {
              cat.classList.remove('search-hidden');
            }
          });

          /* Show/hide no results message */
          if (noResults) {
            if (visibleCount === 0 && query) {
              noResults.classList.add('visible');
            } else {
              noResults.classList.remove('visible');
            }
          }
        });
      }
    });
  }

  /* ─── CART UPSELL QUICK-ADD ───────────────────────────── */
  function initCartUpsellAdd(scope) {
    var btns = scope.querySelectorAll('.nb-cu-tile-btn[data-nb-quick-add]');
    btns.forEach(function(btn) {
      if (btn.dataset.nbInit) return;
      btn.dataset.nbInit = '1';

      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var variantId = btn.dataset.variantId;
        var quantity = parseInt(btn.dataset.quantity, 10) || 1;
        if (!variantId) return;

        btn.disabled = true;
        btn.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] })
        })
        .then(function(r) { return r.json(); })
        .then(function() {
          btn.textContent = 'Added \u2713';
          setTimeout(function() { window.location.reload(); }, 800);
        })
        .catch(function() {
          btn.textContent = 'Error';
          btn.disabled = false;
        });
      });
    });
  }

  /* ─── VIDEO LAZY LOAD (BLOG ARTICLES) ──────────────── */
  function initVideoLazyLoad(scope) {
    var iframes = scope.querySelectorAll('.m-article__content iframe[data-src], .m-article__body iframe[data-src]');
    if (!iframes.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      iframes.forEach(function(iframe) {
        iframe.src = iframe.dataset.src;
      });
      return;
    }

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.src = entry.target.dataset.src;
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    iframes.forEach(function(iframe) { observer.observe(iframe); });
  }

  /* ─── SCROLL-REVEAL (.nb-reveal) ──────────────────────── */
  function initScrollReveal(scope) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var els = scope.querySelectorAll('.nb-reveal:not(.nb-revealed)');
    if (!els.length) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('nb-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function(el) { observer.observe(el); });
  }

  /* ─── INIT ALL ───────────────────────────────────────── */
  function initAll(scope) {
    scope = scope || document;
    initCounters(scope);
    initModeStrips(scope);
    initFeatureTabs(scope);
    initFlipCards(scope);
    initModesToggle(scope);
    initQuickAdd(scope);
    initFaqPage(scope);
    initCartUpsellAdd(scope);
    initVideoLazyLoad(scope);
    initScrollReveal(scope);
  }

  /* Run on page load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { initAll(document); });
  } else {
    initAll(document);
  }

  /* Re-run when Shopify theme editor loads/selects/deselects sections */
  document.addEventListener('shopify:section:load', function(e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:select', function(e) {
    initAll(e.target);
  });
})();
